import type { SearchResultData } from './searchTypes.ts';
import { highlightSegments, typeColor, TYPE_LABELS } from './searchTypes.ts';
import { typeIcon } from './searchIcons.tsx';

// Catalog/order thumbnails are product shots -- rendered on a white, contain-padded frame.
// Everything else is a facility photo -- rendered cover on a dark frame.
const LIGHT_FRAME_TYPES = new Set(['catalogue', 'order']);

export function Highlight({ text, tokens }: { text: string; tokens: string[] }) {
  const segments = highlightSegments(text, tokens);
  return (
    <>
      {segments.map((s, i) => (s.matched ? <mark key={i} className="gs-mark">{s.text}</mark> : <span key={i}>{s.text}</span>))}
    </>
  );
}

export default function SearchResultRow({
  result, tokens, active, onClick, onMouseEnter,
}: {
  result: SearchResultData;
  tokens: string[];
  active: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}) {
  const Icon = typeIcon(result.type);
  const light = LIGHT_FRAME_TYPES.has(result.type);

  return (
    <div
      className={`gs-row${active ? ' active' : ''}`}
      role="option"
      aria-selected={active}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
    >
      <div className={`gs-thumb ${light ? 'gs-thumb-light' : 'gs-thumb-dark'} ${light ? 'gs-thumb-contain' : 'gs-thumb-cover'}`}>
        {result.thumbnailUrl ? (
          <img src={result.thumbnailUrl} alt="" loading="lazy" />
        ) : (
          <div className="gs-empty-icon-frame"><Icon size={20} /></div>
        )}
      </div>
      <div className="gs-row-main">
        <div className="gs-row-title"><Highlight text={result.title} tokens={tokens} /></div>
        <div className="gs-row-sub">
          <span className="gs-type-pill" style={{ background: `${typeColor(result.type)}26`, color: typeColor(result.type) }}>
            {TYPE_LABELS[result.type] ?? result.type}
          </span>
          {result.subtitle && <span><Highlight text={result.subtitle} tokens={tokens} /></span>}
        </div>
      </div>
      <div className="gs-row-right">
        {result.rightLabel && <span className="gs-row-right-label">{result.rightLabel}</span>}
        {result.statusLabel && (
          <span className={result.statusTone === 'warn' ? 'gs-status-warn' : undefined} style={{ color: result.statusTone === 'warn' ? undefined : 'var(--text-muted)' }}>
            {result.statusLabel}
          </span>
        )}
      </div>
    </div>
  );
}
